import React from "react";
import { Box, Button, Flex, HStack, Stack, Text, Icon, Tooltip, Tag } from "@chakra-ui/react";
import {
  FiShield,
  FiCheck,
  FiLock,
  FiTruck,
  FiShoppingBag,
  FiFileText,
  FiMapPin,
  FiPackage,
  FiInbox,
  FiSearch,
  FiAlertCircle,
  FiArrowRight,
  FiStar,
} from "react-icons/fi";
import { colors, gradients, radii, shadows, type, categoryPalette, inferCategory, animations, shimmerBg } from "./designSystem";

/* ----- Global animation style injector (mount once per page) ----- */
export const AnimStyles = () => <style>{animations}</style>;

/* ----- Category dot / pill ----- */
export const CategoryDot = ({ med, size = 10 }) => {
  const key = inferCategory(med);
  const pal = categoryPalette[key] || categoryPalette.generic;
  return <Box w={`${size}px`} h={`${size}px`} borderRadius="full" bg={pal.dot} flexShrink={0} title={pal.label} />;
};

export const CategoryPill = ({ med, size = "xs" }) => {
  const key = inferCategory(med);
  const pal = categoryPalette[key] || categoryPalette.generic;
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap="6px"
      bg={pal.bg}
      color={pal.fg}
      borderRadius="full"
      px={size === "sm" ? 3 : 2.5}
      py="3px"
      fontSize={size === "sm" ? "12px" : "11px"}
      fontWeight="700"
      letterSpacing="0.01em"
      whiteSpace="nowrap"
    >
      <Box w="6px" h="6px" borderRadius="full" bg={pal.dot} />
      {pal.label}
    </Box>
  );
};

/* ----- Rx Required badge — brighter than a tiny red label ----- */
export const RxBadge = ({ onClick }) => (
  <Tooltip label="Valid prescription will be verified by a licensed pharmacist before dispatch." hasArrow placement="top">
    <Box
      as={onClick ? "button" : "div"}
      onClick={onClick}
      display="inline-flex"
      alignItems="center"
      gap="6px"
      bg={colors.accent[50]}
      color={colors.accent[700]}
      border={`1px solid ${colors.accent[100]}`}
      borderRadius="full"
      px={2.5}
      py="3px"
      fontSize="11px"
      fontWeight="800"
      letterSpacing="0.02em"
      cursor={onClick ? "pointer" : "default"}
      _hover={onClick ? { bg: colors.accent[100] } : {}}
    >
      <Icon as={FiShield} boxSize={3} />
      Rx Required
    </Box>
  </Tooltip>
);

/* ----- Verified pharmacy tick ----- */
export const VerifiedBadge = ({ label = "Verified" }) => (
  <Tooltip label="Licensed and verified by PillStack." hasArrow>
    <Box display="inline-flex" alignItems="center" gap="4px" color={colors.success[700]} fontSize="11px" fontWeight="800">
      <Icon as={FiShield} boxSize={3} />
      {label}
    </Box>
  </Tooltip>
);

/* ----- Product image fallback ----- */
export const PillThumbnail = ({ med, size = 64, rounded = "md" }) => {
  const key = inferCategory(med);
  const pal = categoryPalette[key] || categoryPalette.generic;
  const name = (med?.product_name || med?.Name || med?.name || "Medicine").trim();
  const initials = name
    .replace(/[^A-Za-z\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "M";
  return (
    <Box
      w={`${size}px`}
      h={`${size}px`}
      borderRadius={rounded === "full" ? "full" : radii[rounded] || radii.md}
      bg={pal.bg}
      color={pal.fg}
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
      overflow="hidden"
      flexShrink={0}
    >
      <Box
        position="absolute"
        left="10%"
        top="45%"
        w="80%"
        h="14%"
        borderRadius="full"
        bg={pal.dot}
        opacity="0.25"
      />
      <Text fontSize={`${Math.max(11, size * 0.28)}px`} fontWeight="900" zIndex={1} letterSpacing="-0.03em">
        {initials}
      </Text>
    </Box>
  );
};

/* Accepts a medicine record, renders either its image blob or the tinted fallback. */
export const MedicineImage = ({ med, size = 64, rounded = "md" }) => {
  const img = med?.image || med?.Image;
  if (img?.data && img?.contentType) {
    try {
      // buffer may be array or Buffer
      let base64;
      if (typeof img.data === "string") base64 = img.data;
      else if (img.data?.data) base64 = btoa(String.fromCharCode(...new Uint8Array(img.data.data)));
      else if (Array.isArray(img.data)) base64 = btoa(String.fromCharCode(...img.data));
      if (base64) {
        return (
          <Box
            as="img"
            src={`data:${img.contentType};base64, ${base64}`}
            alt={med?.product_name || med?.Name || med?.name || "Medicine"}
            w={`${size}px`}
            h={`${size}px`}
            borderRadius={rounded === "full" ? "full" : radii[rounded] || radii.md}
            objectFit="cover"
            bg={colors.neutral[50]}
            border={`1px solid ${colors.neutral[200]}`}
            loading="lazy"
          />
        );
      }
    } catch {
      // fall through to pill fallback
    }
  }
  return <PillThumbnail med={med} size={size} rounded={rounded} />;
};

/* ----- Empty State ----- */
export const EmptyState = ({
  icon: IconComp = FiInbox,
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  tone = "brand",
}) => {
  const tint = tone === "warning" ? colors.warning[500] : tone === "error" ? colors.error[500] : colors.brand[500];
  const tintBg = tone === "warning" ? colors.warning[50] : tone === "error" ? colors.error[50] : colors.brand[50];
  return (
    <Box
      border={`1.5px dashed ${colors.neutral[300]}`}
      borderRadius={radii.xl}
      p={{ base: 6, md: 10 }}
      textAlign="center"
      bg={colors.neutral[0]}
      maxW="560px"
      mx="auto"
    >
      <Box
        w="88px"
        h="88px"
        borderRadius="full"
        bg={tintBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        mx="auto"
        mb={5}
        color={tint}
      >
        <Icon as={IconComp} boxSize={9} />
      </Box>
      <Text fontSize="22px" fontWeight="800" color={colors.neutral[900]} mb={2} letterSpacing="-0.02em">
        {title}
      </Text>
      {subtitle ? (
        <Text color={colors.neutral[500]} fontSize="15px" mb={6} lineHeight="1.6" maxW="420px" mx="auto">
          {subtitle}
        </Text>
      ) : null}
      <HStack spacing={3} justify="center" wrap="wrap">
        {primaryAction ? (
          <Button
            onClick={primaryAction.onClick}
            bgGradient={gradients.brand}
            color="white"
            size="md"
            px={6}
            fontWeight="800"
            _hover={{ bgGradient: gradients.brandBold, transform: "translateY(-1px)", boxShadow: shadows.brandHover }}
            rightIcon={primaryAction.icon ? <Icon as={primaryAction.icon} /> : <Icon as={FiArrowRight} />}
          >
            {primaryAction.label}
          </Button>
        ) : null}
        {secondaryAction ? (
          <Button
            variant="outline"
            borderColor={colors.neutral[300]}
            color={colors.neutral[700]}
            onClick={secondaryAction.onClick}
            _hover={{ bg: colors.neutral[50] }}
          >
            {secondaryAction.label}
          </Button>
        ) : null}
      </HStack>
    </Box>
  );
};

/* ----- Trust Strip ----- */
export const TrustStrip = ({ compact = false }) => {
  const items = [
    { icon: FiShield, label: compact ? "Licensed" : "Licensed pharmacies" },
    { icon: FiCheck, label: compact ? "AI-verified" : "AI-verified medicines" },
    { icon: FiLock, label: compact ? "Secure" : "Secure checkout" },
    { icon: FiTruck, label: compact ? "45-min" : "45-min delivery" },
  ];
  return (
    <Box
      bg={compact ? colors.brand[50] : colors.brand[700]}
      border={`1px solid ${compact ? colors.brand[100] : colors.brand[800]}`}
      borderRadius={radii.md}
      px={{ base: 3, md: 5 }}
      py={compact ? 2.5 : 3.5}
      overflowX={{ base: "auto", md: "visible" }}
      css={{ "&::-webkit-scrollbar": { display: "none" } }}
    >
      <Flex
        align="center"
        justify={{ base: "flex-start", md: "space-between" }}
        gap={compact ? 3 : 6}
        wrap={{ base: "nowrap", md: "wrap" }}
        minW={{ base: "max-content", md: "auto" }}
      >
        {items.map((it) => (
          <HStack
            key={it.label}
            spacing={2.5}
            color={compact ? colors.brand[800] : "white"}
            flexShrink={0}
          >
            <Icon
              as={it.icon}
              boxSize={compact ? 4 : 5}
              color={compact ? colors.brand[600] : colors.brand[100]}
            />
            <Text
              fontSize={compact ? "12px" : "15px"}
              fontWeight="700"
              m={0}
              whiteSpace="nowrap"
            >
              {it.label}
            </Text>
          </HStack>
        ))}
      </Flex>
    </Box>
  );
};

/* ----- Social proof strip ----- */
export const SocialProof = ({ orderCount = "10,000+" }) => (
  <HStack spacing={2} color={colors.brand[800]} fontSize="12px" fontWeight="700">
    <Icon as={FiStar} color={colors.warning[500]} />
    <Text m={0}>
      Trusted by <strong>{orderCount}</strong> patients nationwide
    </Text>
  </HStack>
);

/* ----- Skeleton row / card ----- */
export const Skeleton = ({ w = "100%", h = "14px", rounded = "sm", mb = 0 }) => (
  <>
    <style>{animations}</style>
    <Box
      w={w}
      h={h}
      mb={mb}
      borderRadius={radii[rounded] || radii.sm}
      style={{
        background: shimmerBg,
        backgroundSize: "800px 100%",
        animation: "pillstack-shimmer 1.4s linear infinite",
      }}
    />
  </>
);

export const SkeletonCard = () => (
  <Box bg="white" borderRadius={radii.lg} border={`1px solid ${colors.neutral[200]}`} p={4}>
    <Skeleton h="160px" rounded="md" mb={3} />
    <Skeleton w="60%" h="14px" mb={2} />
    <Skeleton w="40%" h="12px" mb={3} />
    <Skeleton w="30%" h="28px" rounded="full" />
  </Box>
);

/* ----- Generic section header with optional CTA ----- */
export const SectionHeader = ({ eyebrow, title, description, action }) => (
  <Flex align="flex-end" justify="space-between" mb={4} gap={3} wrap="wrap">
    <Box>
      {eyebrow ? (
        <Text
          fontSize="11px"
          fontWeight="800"
          color={colors.brand[600]}
          textTransform="uppercase"
          letterSpacing="0.12em"
          m={0}
          mb={1}
        >
          {eyebrow}
        </Text>
      ) : null}
      <Text fontSize="24px" fontWeight="800" color={colors.neutral[900]} m={0} letterSpacing="-0.02em">
        {title}
      </Text>
      {description ? (
        <Text color={colors.neutral[500]} fontSize="14px" mt={1} m={0}>
          {description}
        </Text>
      ) : null}
    </Box>
    {action ? <Box>{action}</Box> : null}
  </Flex>
);

/* ----- Quantity Stepper (44px tap targets) ----- */
export const QuantityStepper = ({ value = 1, onChange, min = 1, max = 99 }) => {
  const dec = () => onChange?.(Math.max(min, value - 1));
  const inc = () => onChange?.(Math.min(max, value + 1));
  return (
    <HStack
      spacing={0}
      bg={colors.neutral[50]}
      borderRadius="full"
      border={`1px solid ${colors.neutral[200]}`}
      p="4px"
    >
      <Box
        as="button"
        type="button"
        onClick={dec}
        w="36px"
        h="36px"
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color={colors.neutral[700]}
        fontWeight="800"
        fontSize="18px"
        bg="white"
        border={`1px solid ${colors.neutral[200]}`}
        aria-label="decrease quantity"
        _hover={{ bg: colors.neutral[100] }}
        _active={{ transform: "scale(0.95)" }}
      >
        −
      </Box>
      <Text minW="36px" textAlign="center" fontWeight="800" color={colors.neutral[900]} m={0}>
        {value}
      </Text>
      <Box
        as="button"
        type="button"
        onClick={inc}
        w="36px"
        h="36px"
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="white"
        fontWeight="800"
        fontSize="18px"
        bgGradient={gradients.brand}
        aria-label="increase quantity"
        _hover={{ bgGradient: gradients.brandBold }}
        _active={{ transform: "scale(0.95)" }}
      >
        +
      </Box>
    </HStack>
  );
};

/* ----- Price display with optional MRP strike ----- */
export const PriceTag = ({ price, mrp, size = "md" }) => {
  const savings = mrp && price && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  return (
    <HStack spacing={2} align="baseline">
      <Text
        fontSize={size === "lg" ? "20px" : size === "sm" ? "14px" : "16px"}
        fontWeight="900"
        color={colors.brand[700]}
        m={0}
      >
        ₹{Number(price || 0).toFixed(0)}
      </Text>
      {savings > 0 ? (
        <>
          <Text as="s" fontSize={size === "sm" ? "11px" : "13px"} color={colors.neutral[400]} m={0}>
            ₹{Number(mrp).toFixed(0)}
          </Text>
          <Box
            fontSize="10px"
            fontWeight="800"
            color={colors.success[700]}
            bg={colors.success[50]}
            px={1.5}
            py="2px"
            borderRadius="full"
          >
            {savings}% OFF
          </Box>
        </>
      ) : null}
    </HStack>
  );
};

/* ----- Availability dot ----- */
export const StockDot = ({ inStock = true, label }) => (
  <HStack spacing={1.5}>
    <Box w="8px" h="8px" borderRadius="full" bg={inStock ? colors.success[500] : colors.error[500]} />
    <Text fontSize="11px" fontWeight="700" color={inStock ? colors.success[700] : colors.error[700]} m={0}>
      {label || (inStock ? "In stock" : "Out of stock")}
    </Text>
  </HStack>
);

/* ----- Error State ----- */
export const ErrorState = ({ title = "Something went wrong", subtitle, retry, suggestion }) => (
  <EmptyState
    icon={FiAlertCircle}
    title={title}
    subtitle={subtitle || "We couldn't complete that action. Please try again in a moment."}
    tone="error"
    primaryAction={retry ? { label: "Try again", onClick: retry } : null}
    secondaryAction={suggestion}
  />
);

export default {
  AnimStyles,
  CategoryDot,
  CategoryPill,
  RxBadge,
  VerifiedBadge,
  PillThumbnail,
  MedicineImage,
  EmptyState,
  TrustStrip,
  SocialProof,
  Skeleton,
  SkeletonCard,
  SectionHeader,
  QuantityStepper,
  PriceTag,
  StockDot,
  ErrorState,
};
